import { extractErrorMessage, ApiError } from './errors';
import { createRefreshQueue } from './refresh-queue';

const REQUEST_TIMEOUT_MS = 30_000;

export interface HttpClientOptions {
  baseUrl: string;
  /** Lê o access token atual da sessão em memória (C2 auth). Nunca lê de localStorage. */
  getAccessToken: () => string | null;
  /** Dispara POST /auth/refresh (cookie HttpOnly) e atualiza a sessão em memória. */
  refreshSession: () => Promise<void>;
  onSessionExpired: () => void;
  getCsrfToken?: () => string | null;
  ensureCsrfToken?: () => Promise<void>;
  canSendRequest?: (path: string) => boolean;
}

export interface HttpClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  delete<T>(path: string, init?: RequestInit): Promise<T>;
}

function correlationId(): string {
  return crypto.randomUUID();
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const runRefresh = createRefreshQueue(options.refreshSession);

  async function send<T>(method: string, path: string, body: unknown, init: RequestInit | undefined, retried: boolean): Promise<T> {
    if (options.canSendRequest?.(path) === false) {
      throw new ApiError({ status: 401, kind: 'http', correlationId: undefined, message: 'A sessão foi encerrada. Entre novamente.' });
    }
    if (method !== 'GET' && method !== 'HEAD' && options.getCsrfToken?.() == null) {
      await options.ensureCsrfToken?.();
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    const headers = new Headers(init?.headers);
    headers.set('X-Correlation-Id', correlationId());
    headers.set('Accept', 'application/json');
    const token = options.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      const csrfToken = options.getCsrfToken?.();
      if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
    }
    const multipart = body instanceof FormData;
    if (multipart) headers.delete('Content-Type');
    else if (body !== undefined) headers.set('Content-Type', 'application/json');

    const requestInit: RequestInit = {
      ...init,
      method,
      headers,
      credentials: 'include',
      signal: controller.signal,
      // exactOptionalPropertyTypes: `body` só entra no objeto quando existe — nunca `body: undefined`.
      ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }),
    };

    let response: Response;
    try {
      response = await fetch(`${options.baseUrl}${path}`, requestInit);
    } catch (cause) {
      clearTimeout(timeout);
      const timedOut = cause instanceof DOMException && cause.name === 'AbortError';
      throw new ApiError({
        status: 0,
        correlationId: undefined,
        kind: timedOut ? 'timeout' : 'network',
        message: timedOut
          ? 'A requisição demorou demais. Tente novamente.'
          : 'Não foi possível conectar. Verifique sua rede ou tente novamente.',
      });
    }
    clearTimeout(timeout);

    if (response.status === 401 && !retried) {
      await runRefresh();
      return send<T>(method, path, body, init, true);
    }

    if (response.status === 401 && retried) {
      options.onSessionExpired();
    }

    if (!response.ok) {
      const responseBody: unknown = await response.json().catch(() => undefined);
      const retryAfter = response.headers.get('Retry-After');
      const retryAfterMs = retryAfter === null ? undefined : /^\d+$/.test(retryAfter)
        ? Number(retryAfter) * 1000 : Math.max(0, Date.parse(retryAfter) - Date.now());
      throw new ApiError({
        status: response.status,
        correlationId: response.headers.get('X-Correlation-Id') ?? undefined,
        kind: 'http',
        retryAfterMs: Number.isFinite(retryAfterMs) ? retryAfterMs : undefined,
        message: extractErrorMessage(response.status, responseBody),
      });
    }

    if (response.status === 204) {
      // T é sempre `unknown` nos call sites reais (get<unknown>, parseado por Zod em C4 — ver
      // typescript-tipagem-estrita/SKILL.md); o cast genérico é a fronteira do httpClient, não um
      // atalho de tipagem em código de produto.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return undefined as T;
    }
    const json: unknown = await response.json();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return json as T;
  }

  return {
    get: (path, init) => send('GET', path, undefined, init, false),
    post: (path, body, init) => send('POST', path, body, init, false),
    put: (path, body, init) => send('PUT', path, body, init, false),
    delete: (path, init) => send('DELETE', path, undefined, init, false),
  };
}
